import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import InlineNotice from '../components/InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import { formatCurrency as formatCurrencyBySystem, getCurrencyPrefixFromStorage, getNumberingSystemFromStorage } from '../utils/numberingSystem';

/**
 * Unified Payment Modal Component
 * Handles payments for invoices, expenses, and purchases
 * 
 * Props:
 * - isOpen: boolean - controls modal visibility
 * - onClose: function - callback when modal closes
 * - paymentType: 'invoice' | 'expense' | 'purchase' - type of payment
 * - entity: object - the invoice, expense, or purchase object
 * - onPaymentSaved: function - callback when payment is saved
 */
const UnifiedPaymentModal = ({ isOpen, onClose, paymentType, entity, onPaymentSaved }) => {
  const [amount, setAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payments, setPayments] = useState([]);
  const [fetchingPayments, setFetchingPayments] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const { showInlineNotice } = useInlineNotice();

  // Get entity ID based on payment type
  const getEntityId = () => {
    if (paymentType === 'invoice') return entity;
    return entity?._id;
  };

  // Get entity total amount
  const getEntityTotal = () => {
    if (!entity) return 0;
    if (paymentType === 'expense') return entity.amount;
    if (paymentType === 'invoice') return paymentData?.invoiceTotal || 0;
    return entity.total; // purchase
  };

  // Get total paid
  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const remaining = getEntityTotal() - totalPaid;
  const isFullyPaid = remaining <= 0;

  // Get title based on payment type
  const getTitle = () => {
    const entityLabel = paymentType === 'invoice' ? 'Invoice' : paymentType.charAt(0).toUpperCase() + paymentType.slice(1);
    const actionLabel = paymentType === 'invoice' ? 'Credit' : 'Payment';
    
    if (isFullyPaid) {
      return `${entityLabel} ${actionLabel} Details`;
    }
    return `Record ${actionLabel} - ${entityLabel}`;
  };

  // Fetch payments when modal opens
  useEffect(() => {
    if (isOpen && entity) {
      fetchPayments();
      setAmount('');
      setPaymentReference('');
      setPaymentMode('Cash');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setError('');
    }
  }, [isOpen, entity, paymentType]);

  const fetchPayments = async () => {
    const entityId = getEntityId();
    if (!entityId) return;
    
    setFetchingPayments(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/payments/${paymentType}/${entityId}`);
      setPayments(response.data.payments || []);
      setPaymentData(response.data);
    } catch (err) {
      console.error('Error fetching payments:', err);
      if (err.response?.status !== 404) {
        setError('Failed to load payment history');
      }
    } finally {
      setFetchingPayments(false);
    }
  };

  const handleAddPayment = async () => {
    const paymentAmount = parseFloat(amount);
    
    if (!amount || isNaN(paymentAmount) || paymentAmount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }

    if (paymentAmount > remaining) {
      setError(`Payment amount cannot exceed remaining amount (${formatAmount(remaining)})`);
      return;
    }

    if (!paymentReference.trim()) {
      setError('Payment reference is required');
      return;
    }

    if (!paymentMode) {
      setError('Please select a payment mode');
      return;
    }

    if (!paymentDate) {
      setError('Please select a payment date');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const entityId = getEntityId();
      await axios.post(
        `${API_BASE_URL}/payments/${paymentType}/${entityId}`,
        {
          amount: paymentAmount,
          paymentReference: paymentReference.trim(),
          paymentMode,
          paymentDate
        }
      );

      const terminology = paymentType === 'invoice' ? 'Credit' : 'Payment';
      showInlineNotice(`${terminology} recorded successfully.`, {
        scope: `payments-${paymentType}`
      });
      
      // Refresh payments list
      await fetchPayments();
      
      // Reset form
      setAmount('');
      setPaymentReference('');
      setPaymentMode('Cash');
      setPaymentDate(new Date().toISOString().split('T')[0]);

      // Notify parent
      if (onPaymentSaved) {
        onPaymentSaved();
      }

      // Check if fully paid and close modal
      const newTotalPaid = totalPaid + paymentAmount;
      if (newTotalPaid >= getEntityTotal()) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Error adding payment:', err);
      const errorMsg = err.response?.data?.error || 
        err.response?.data?.details || 
        'Failed to record payment. Please try again.';
      setError(errorMsg);
      showInlineNotice(errorMsg, {
        scope: `payments-${paymentType}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) {
      return;
    }

    setDeletingPayment(paymentId);
    try {
      await axios.delete(`${API_BASE_URL}/payments/${paymentId}`);
      
      const terminology = paymentType === 'invoice' ? 'Credit' : 'Payment';
      showInlineNotice(`${terminology} deleted successfully.`, {
        scope: `payments-${paymentType}`
      });
      
      // Refresh payments list
      await fetchPayments();
      
      // Notify parent
      if (onPaymentSaved) {
        onPaymentSaved();
      }
    } catch (err) {
      console.error('Error deleting payment:', err);
      const errorMsg = err.response?.data?.error || 
        'Failed to delete payment. Please try again.';
      setError(errorMsg);
      showInlineNotice(errorMsg, {
        scope: `payments-${paymentType}`,
        type: 'error'
      });
    } finally {
      setDeletingPayment(null);
    }
  };

  const formatAmount = (amount) => {
    return formatCurrencyBySystem(Number(amount || 0), getNumberingSystemFromStorage(), {
      prefix: getCurrencyPrefixFromStorage()
    });
  };
  const currencyPrefix = getCurrencyPrefixFromStorage();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen || !entity) return null;

  // Entity-specific summary info
  const renderEntitySummary = () => {
    if (paymentType === 'expense') {
      return (
        <div style={{ marginBottom: '8px', fontSize: '14px', color: '#6b7280' }}>
          {entity.expenseNumber && (
            <div>Expense #: <strong style={{ color: '#1f2937' }}>{entity.expenseNumber}</strong></div>
          )}
          <div style={{ marginTop: '4px' }}>Type: <strong style={{ color: '#1f2937' }}>{entity.type}</strong></div>
          <div style={{ marginTop: '4px' }}>Employee: <strong style={{ color: '#1f2937' }}>{entity.employee?.name || 'N/A'}</strong></div>
          <div style={{ marginTop: '4px' }}>Date: <strong style={{ color: '#1f2937' }}>{formatDate(entity.date)}</strong></div>
        </div>
      );
    } else if (paymentType === 'purchase') {
      return (
        <div style={{ marginBottom: '8px', fontSize: '14px', color: '#6b7280' }}>
          {entity.purchaseNumber && (
            <div>Purchase #: <strong style={{ color: '#1f2937' }}>{entity.purchaseNumber}</strong></div>
          )}
          <div>Vendor: <strong style={{ color: '#1f2937' }}>{entity.vendorName || entity.vendor?.name}</strong></div>
          {entity.gst && <div style={{ marginTop: '4px' }}>GST: <strong style={{ color: '#1f2937' }}>{entity.gst}</strong></div>}
          <div style={{ marginTop: '4px' }}>Date: <strong style={{ color: '#1f2937' }}>{formatDate(entity.date)}</strong></div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{getTitle()}</h2>
          <button className="modal-close" onClick={handleClose} disabled={loading}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="modal-body" style={{ padding: '24px' }}>
          <InlineNotice scope={`payments-${paymentType}`} />
          {fetchingPayments && (
            <div style={{
              backgroundColor: '#f3f4f6',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '16px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                <path d="M3 21v-5h5"/>
              </svg>
              Loading payment information...
            </div>
          )}

          {/* Summary Box */}
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '8px', color: '#0369a1' }}>
              {paymentType.charAt(0).toUpperCase() + paymentType.slice(1)} Summary
            </div>
            
            {renderEntitySummary()}
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '12px',
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: '1px solid #bae6fd'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                  Total Amount
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                  {formatAmount(getEntityTotal())}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                  Paid
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#10b981' }}>
                  {formatAmount(totalPaid)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                  Remaining
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: isFullyPaid ? '#10b981' : '#dc2626' }}>
                  {formatAmount(remaining)}
                </div>
              </div>
            </div>
          </div>

          {isFullyPaid && (
            <div style={{
              backgroundColor: '#dcfce7',
              border: '1px solid #bbf7d0',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '16px',
              color: '#16a34a',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span>This {paymentType} has been fully {paymentType === 'invoice' ? 'credited' : 'paid'}.</span>
            </div>
          )}

          {error && (
            <div style={{
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '16px',
              color: '#dc2626',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {/* Payment Form - Only show if not fully paid */}
          {!isFullyPaid && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px',
                  color: '#1f2937'
                }}>
                  {paymentType === 'invoice' ? 'Credit' : 'Payment'} Amount ({currencyPrefix}) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={remaining}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter payment amount"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${error && !amount ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: loading ? '#f3f4f6' : '#fff'
                  }}
                />
                {remaining > 0 && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Maximum: {formatAmount(remaining)}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px',
                  color: '#1f2937'
                }}>
                  {paymentType === 'invoice' ? 'Credit' : 'Payment'} Reference *
                </label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Transaction ID / Cheque No."
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${error && !paymentReference ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: loading ? '#f3f4f6' : '#fff'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px',
                  color: '#1f2937'
                }}>
                  {paymentType === 'invoice' ? 'Credit' : 'Payment'} Mode *
                </label>
                <select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: loading ? '#f3f4f6' : '#fff'
                  }}
                >
                  <option value="Cash">Cash</option>
                  <option value="Gpay">Google Pay</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px',
                  color: '#1f2937'
                }}>
                  {paymentType === 'invoice' ? 'Credit' : 'Payment'} Date *
                </label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${error && !paymentDate ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: loading ? '#f3f4f6' : '#fff'
                  }}
                />
              </div>

              <button
                onClick={handleAddPayment}
                disabled={loading || !amount || !paymentMode || !paymentDate || !paymentReference}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (loading || !amount || !paymentMode || !paymentDate || !paymentReference) ? 'not-allowed' : 'pointer',
                  opacity: (loading || !amount || !paymentMode || !paymentDate || !paymentReference) ? 0.6 : 1,
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loading && (
                  <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                    <path d="M3 21v-5h5"/>
                  </svg>
                )}
                {loading ? `Recording ${paymentType === 'invoice' ? 'Credit' : 'Payment'}...` : `Record ${paymentType === 'invoice' ? 'Credit' : 'Payment'}`}
              </button>
            </>
          )}

          {/* Payment History */}
          {payments.length > 0 && (
            <div style={{ marginTop: isFullyPaid ? '0' : '24px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '12px',
                color: '#1f2937'
              }}>
                {paymentType === 'invoice' ? 'Credit' : 'Payment'} History ({payments.length})
              </h3>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {payments.map((payment) => (
                  <div
                    key={payment._id}
                    style={{
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '8px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                          {formatAmount(payment.amount)}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                          {payment.paymentMode} {payment.paymentReference && `• ${payment.paymentReference}`}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeletePayment(payment._id)}
                        disabled={deletingPayment === payment._id}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: deletingPayment === payment._id ? 'not-allowed' : 'pointer',
                          opacity: deletingPayment === payment._id ? 0.6 : 1
                        }}
                        title={`Delete ${paymentType === 'invoice' ? 'credit' : 'payment'}`}
                      >
                        {deletingPayment === payment._id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      <div>{paymentType === 'invoice' ? 'Credit' : 'Payment'} Date: {formatDate(payment.paymentDate)}</div>
                      <div>Recorded by: {payment.createdBy?.name || 'Unknown'}</div>
                      <div>On: {formatDateTime(payment.createdAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              color: '#1f2937',
              opacity: loading ? 0.6 : 1
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnifiedPaymentModal;
